/* eslint-disable react/no-unused-prop-types */
import { Disclosure } from 'components/disclosure/Disclosure';
import { Checkbox, CheckboxChangeEvent } from 'components/forms/Checkbox';
import { SelectAllCheckbox } from 'components/forms/multiselect/SelectAllCheckbox';
import * as React from 'react';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { groupBy } from 'utils/array';

// NOTE: In this file, TValue refers to the generic type associated with options.
// In most cases, this is just the string guid or number associated with the entity.

/**
 * Valid types for the value of an option
 *
 * Example:
 * ```ts
 * const value1: OptionValue = 534800;
 * const value2: OptionValue = '4d7abdaf-da00-46a7-abdb-dc902fa30fd8';
 * ```
 */
export type OptionValue = string | number;

/**
 * The type of a selected option. A selection can be specified as just a value
 * or the full object which includes the value.
 *
 * Example:
 * ```ts
 * const selectedNoTitle: Selection[] = [100, 114, 567];
 * const selectedWithTitle: Selection[] = [
 *     { title: 'A', value: 100 },
 *     { title: 'B', value: 114 },
 *     { title: 'C', value: 567 },
 * ]
 * ```
 */
export type Selection<TValue extends OptionValue> = TValue | MultiSelectOption<TValue>;

/**
 * A singular multiselect option with a title and a value.
 * @example { title: 'Stamping Press', value: '4d7abdaf-da00-46a7-abdb-dc902fa30fd8' },
 * @example { title: 'Fan 1', value: 39785, label: <FormattedLabel>Fan 1</FormattedLabel> },
 */
export type MultiSelectOption<TValue extends OptionValue> = {
    /**
     * Value displayed (unless `label` is supplied) and used for filtering.
     */
    title: string;

    /**
     * The option value.
     */
    value: TValue;

    /**
     * Custom render.
     */
    label?: ReactNode;
};

/**
 * A singular multiselect option with a title and a value, associated with a
 * group of options.
 *
 * Example:
 * ```ts
 * const groupOption1: MultiSelectOptionWithGroup<string> = {
 *     group: 'Automotive',
 *     title: 'Stamping Press',
 *     value: '4d7abdaf-da00-46a7-abdb-dc902fa30fd8'
 * };
 * const groupOption2: MultiSelectOptionWithGroup<number> = {
 *     group: 'Painting',
 *     title: 'Fan 1',
 *     value: 39785
 * };
 * ```
 */
export type MultiSelectOptionWithGroup<
    TValue extends OptionValue
> = MultiSelectOption<TValue> & {
    group: string;
};

/**
 * A named group of multiselect options
 *
 * Example:
 * ```ts
 * {
 *     title: 'TMMC Cambridge',
 *     options: [
 *         { title: 'North Assembly', value: 16238 },
 *         { title: 'North Paint', value: 8983 },
 *         { title: 'North Weld', value: 12005 },
 *         { title: 'Plastics', value: 14034 },
 *     ],
 * },
 * ```
 */
export type MultiSelectGroup<TValue extends OptionValue> = {
    title: string;
    options: MultiSelectOption<TValue>[];
};

/**
 * The data passed to an event handler when a multiselect event occurs.
 */
export type MultiSelectEvent<TOption> = {
    /**
     * The currently selected values, after any changes have been made
     */
    selected: TOption[];
};

/**
 * The data passed to a custom checkbox callback.
 */
export type MultiSelectCustomCheckboxInfo<TValue extends OptionValue> = Readonly<{
    /**
     * The multi select option.
     */
    option: MultiSelectOption<TValue>;
    /**
     * If the option is selected.
     */
    isSelected: boolean;
    /**
     * The index of the option.
     */
    index: number;
    /**
     * The option is enabled (can be checked or unchecked).  An item is disabled if it is
     * unchecked and the max selections has been reached.  Will always be true if no max
     * selections option is specified.
     */
    isEnabled: boolean;
}>;

/**
 * Props that are common to all multiselects
 */
type MultiSelectCommonOptions<TValue extends OptionValue> = {
    /**
     * The heading for the multiselect list that will be displayed before the
     * list of options.
     */
    title?: string;
    /**
     * Values that should be preselected on the initial render.
     */
    selectedOptions: Selection<TValue>[];
    className?: string;
    /**
     * A filter to narrow down the options displayed. Any option which matches
     * the filter will be displayed, and other options are not displayed.
     */
    filter?: string;
    /**
     * Optional custom React Node that will override the default checkbox.
     * Accepts a selected state, and the index of the option for any custom rendering options.
     */
    customCheckbox?: (info: MultiSelectCustomCheckboxInfo<TValue>) => ReactNode;
    /**
     * A class to apply to all options in the multiselect
     */
    optionClass?: string;
    /**
     * The maximum allowed selections.
     */
    maxSelections?: number;
};

/**
 * The props for a multiselect which has grouped options.
 */
export type MultiSelectGroupedProps<
    TValue extends OptionValue
> = MultiSelectCommonOptions<TValue> & {
    groups: MultiSelectGroup<TValue>[];
    filterOnGroupTitle?: boolean;
    /**
     * Change event handler that returns the currently selected options after changes
     */
    onChange?: (event: MultiSelectEvent<MultiSelectOptionWithGroup<TValue>>) => void;
};

/**
 * The props for a multiselect which is just a flat list of options.
 */
export type MultiSelectProps<
    TValue extends OptionValue
> = MultiSelectCommonOptions<TValue> & {
    options: MultiSelectOption<TValue>[];
    /**
     * Change event handler that returns the currently selected options after changes
     */
    onChange: (event: MultiSelectEvent<MultiSelectOption<TValue>>) => void;

    singleSelect?: boolean;

    /**
     * Whether or not to show the select/deselect all checkbox
     */
    showSelectAll?: boolean;

    /**
     * Whether or not to treat a normal click as single-select and
     * CTRL + Click as multi-select
     */
    respectControlKey?: boolean;

    /**
     * Whether the label element should only be as large as necessary to fit its
     * content, or the full width available. Defaults false.
     */
    labelPrefersMinWidth?: boolean;
};

/**
 * The props for a multiselect which is just a flat list of options.
 */
export type MultiSelectWithGroupOptionProps<
    TValue extends OptionValue
> = MultiSelectCommonOptions<TValue> & {
    options: MultiSelectOptionWithGroup<TValue>[];
    /**
     * Change event handler that returns the currently selected options after changes
     */
    onChange?: (event: MultiSelectEvent<MultiSelectOptionWithGroup<TValue>>) => void;

    singleSelect?: boolean;
};

const Options = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const ButtonMultiSelectOptions = styled.div`
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    flex-wrap: wrap;
`;

const ButtonMultiSelectContainer = styled.span`
    flex: 1 1 0px;
    min-width: 33%;
`;

const Option = styled.li``;

const ListHeader = styled.header`
    font-weight: 500;
    padding-bottom: 1rem;
`;

const SelectableButton = styled.span<{ $selected: boolean; $maxColumns?: number }>`
    color: ${(props) => (props.$selected ? tokens.text.onColor : tokens.text.primary)};
    padding: 2px 8px;
    text-align: center;
    font-size: 1.2rem;
    flex: 1 1 0px;
    min-width: 33%;
    background-color: ${(props) =>
        props.$selected ? tokens.primary.default : tokens.background.default};
    border: 1px solid ${tokens.divider.default};
    margin: 1px;
    text-align: center;
`;

/**
 * Type guard to determine if a value is a valid `OptionValue`.
 */
function isOptionValue(val: unknown): val is OptionValue {
    return typeof val === 'number' || typeof val === 'string';
}

/**
 * Type guard to determine if an object is a valid `MultiSelectOption`.
 * @param option The value that will be checked to see if it is a `MultiSelectOption`.
 */
function isMultiSelectOption<TValue extends OptionValue>(
    option: unknown
): option is MultiSelectOption<TValue> {
    // Type assertion here is OK, because we are explicitly checking its types
    // and values manually in this function
    const opt = option as MultiSelectOption<TValue>;
    return (
        typeof opt === 'object' &&
        typeof opt.title === 'string' &&
        isOptionValue(opt.value)
    );
}

/**
 * Returns a Map which maps selected values to true, allowing fast lookup. Used
 * to optimize bulk lookup operations.
 * @param selected The selected values to create a map from.
 */
function getSelectionMap<TValue extends OptionValue>(
    selected: Selection<TValue>[]
): Map<TValue, boolean> {
    const map = new Map<TValue, boolean>();
    selected.forEach((selection) => {
        if (isMultiSelectOption(selection)) {
            map.set(selection.value, true);
        } else {
            map.set(selection, true);
        }
    });
    return map;
}

/**
 * Returns if an option is selected in a list of selected options.
 * @param selectedOptions The list of selected options to look in.
 * @param option The option that will be checked too see if it is selected.
 */
export function isOptionSelected<TValue extends OptionValue>(
    selectedOptions: Selection<TValue>[],
    option: MultiSelectOption<TValue>
): boolean {
    return selectedOptions.some((selection) => {
        if (isMultiSelectOption(selection)) {
            return selection.value === option.value;
        }
        return selection === option.value;
    });
}

/**
 * Returns a flat array of selected options with the group name added as a property.
 * @param groups Array of all groups and data in the multiselect.
 * @param selected The currently selected options or values that will be associated with a group.
 */
function getSelectedOptionsWithGroup<TValue extends OptionValue>(
    groups: MultiSelectGroup<TValue>[],
    selected: Selection<TValue>[]
) {
    const selectedMap = getSelectionMap(selected);

    return (
        groups
            // Turn each group into a list of options and flatten all grouped
            // options into a single list
            .flatMap((group) =>
                group.options
                    // Filter out non-selected options
                    .filter((option) => selectedMap.get(option.value))
                    // Add group title to each option
                    .map((option) => ({
                        group: group.title,
                        title: option.title,
                        value: option.value,
                    }))
            )
    );
}

/**
 * Returns an array of groups with options within each group.
 * @param options Array of all options in the multiselect.
 */
export function getMultiSelectGroupsFromOptions(
    options: MultiSelectOptionWithGroup<string>[]
): MultiSelectGroup<string>[] {
    const groups = groupBy(options, (option) => option.group);
    return Object.entries(groups).map<MultiSelectGroup<string>>(([title, option]) => ({
        title,
        options: option as MultiSelectOption<string>[],
    }));
}

/**
 * Returns a flat array of selected options from a list of currently selected options.
 * The difference between this function and the `selectedOptions` property is that this
 * will always return the full option object, not just the value.
 * @param options All possible multiselect options.
 * @param selected The currently selected options or values.
 */
function getSelectedOptions<TValue extends OptionValue>(
    options: MultiSelectOption<TValue>[],
    selected: Selection<TValue>[]
) {
    const selectedMap = getSelectionMap(selected);
    return options.filter((option) => selectedMap.get(option.value));
}

/**
 * Returns a flat array of selected options from a list of currently selected options.
 * The difference between this function and the `selectedOptions` property is that this
 * will always return the full option object, not just the value.
 * @param options All possible multiselect options.
 * @param selected The currently selected options or values.
 */
function getSelectedOptionsWithGroupOption<TValue extends OptionValue>(
    options: MultiSelectOptionWithGroup<TValue>[],
    selected: Selection<TValue>[]
) {
    const selectedMap = getSelectionMap(selected);
    return options.filter((option) => selectedMap.get(option.value));
}

/**
 * Creates a function that returns true if the supplied string is contained in `filterText` (case insensitive).
 * If `filterText` is not a non-empty string, `undefined` will be returned.
 *
 * @param filterText The text that the filter function will use.
 */
const buildFilter = (
    filterText: string | null | undefined
): ((title: string) => boolean) | undefined => {
    if (filterText && filterText.length > 0) {
        const lowerText = filterText.toLowerCase();
        return (title: string) => title.toLowerCase().includes(lowerText);
    }
    return undefined;
};

export function buildOnChangeCheckbox<TValue extends OptionValue>(
    singleSelect: boolean | undefined,
    options: MultiSelectOption<TValue>[],
    selectedOptions: Selection<TValue>[],
    onChange: (event: MultiSelectEvent<MultiSelectOption<TValue>>) => void,
    respectControlKey?: boolean
) {
    return (
        event: CheckboxChangeEvent | undefined,
        value: TValue,
        ctrlPressed: boolean | undefined
    ) => {
        const ctrlKey =
            ctrlPressed || event?.nativeEvent.ctrlKey || event?.nativeEvent.metaKey;
        let newSelected = selectedOptions;
        if (singleSelect || (respectControlKey && !ctrlKey)) {
            // do single select
            if (selectedOptions.length > 1) {
                // Handles the case where many are selected, and one of these selected items
                //  is single-selected (relevant when respectControlKey is true)
                newSelected = [value];
            } else {
                newSelected = value === selectedOptions[0] ? [] : [value];
            }
        } else if (event?.value || ctrlKey) {
            // Add/remove selected options for multiselect
            const selectedItemExists = selectedOptions.find((item) => item === value);

            // If item doesn't exist in list, add it
            // otherwise the second click will remove it
            // (has to be set up this way for CTRL+Click functionality)
            if (!selectedItemExists) {
                newSelected = [...selectedOptions, value];
            } else {
                newSelected = selectedOptions.filter((selection) => {
                    if (isMultiSelectOption(selection)) {
                        return selection.value !== value;
                    }
                    return selection !== value;
                });
            }
        } else {
            // Remove selected item for single select
            newSelected = selectedOptions.filter((selection) => {
                if (isMultiSelectOption(selection)) {
                    return selection.value !== value;
                }
                return selection !== value;
            });
        }

        onChange({
            selected: getSelectedOptions(options, newSelected),
        });
    };
}

/**
 * A multiselect with groups of data.
 */
export function MultiSelectGrouped<TValue extends OptionValue>({
    title,
    groups,
    selectedOptions,
    onChange,
    className,
    filter,
    customCheckbox,
    maxSelections,
    optionClass,
    filterOnGroupTitle = false,
}: MultiSelectGroupedProps<TValue>) {
    const onChangeCheckbox = (event: CheckboxChangeEvent, value: TValue) => {
        let newSelected = selectedOptions;

        if (event.value) {
            // Add to selected options
            newSelected = [...selectedOptions, value];
        } else {
            // Remove from selected options
            newSelected = selectedOptions.filter((selection) => {
                if (isMultiSelectOption(selection)) {
                    return selection.value !== value;
                }
                return selection !== value;
            });
        }

        if (onChange) {
            onChange({
                selected: getSelectedOptionsWithGroup(groups, newSelected),
            });
        }
    };

    const itemFilter = buildFilter(filter);
    const hasMaxSelected = selectedOptions.length === maxSelections;

    return (
        <div className={className}>
            <ListHeader>{title}</ListHeader>
            {groups.map((group) => {
                const isGroupTitleFiltered =
                    itemFilter === undefined ||
                    (filterOnGroupTitle && itemFilter(group.title));
                const isGroupVisible =
                    itemFilter === undefined ||
                    isGroupTitleFiltered ||
                    group.options.some((option) => itemFilter(option.title));
                return (
                    <Disclosure
                        key={group.title}
                        summary={
                            <span role="heading" aria-level={2} className="bold">
                                {group.title}
                            </span>
                        }
                        summaryClassName="px-0 py-5"
                        style={{ display: isGroupVisible ? 'block' : 'none' }}
                    >
                        <Options>
                            {group.options.map((option, index) => {
                                const isSelected = isOptionSelected(
                                    selectedOptions,
                                    option
                                );
                                const isEnabled = isSelected || !hasMaxSelected;
                                const pointerClass = isEnabled ? 'cursor-pointer' : '';
                                const isOptionVisible =
                                    itemFilter === undefined ||
                                    isGroupTitleFiltered ||
                                    itemFilter(option.title);

                                return (
                                    <Option
                                        key={option.value}
                                        style={{
                                            display: isOptionVisible ? 'block' : 'none',
                                        }}
                                        className={`${optionClass ?? ''} py-5`}
                                    >
                                        <label
                                            className={`flex align-start ${pointerClass}`}
                                        >
                                            {customCheckbox &&
                                                customCheckbox({
                                                    option,
                                                    isSelected,
                                                    isEnabled,
                                                    index,
                                                })}
                                            <Checkbox
                                                onChange={
                                                    isEnabled
                                                        ? (event) =>
                                                              onChangeCheckbox(
                                                                  event,
                                                                  option.value
                                                              )
                                                        : undefined
                                                }
                                                checked={isOptionSelected(
                                                    selectedOptions,
                                                    option
                                                )}
                                                hidden={!!customCheckbox}
                                                label={option.title}
                                            />
                                        </label>
                                    </Option>
                                );
                            })}
                        </Options>
                    </Disclosure>
                );
            })}
        </div>
    );
}

const StyledLabel = styled.label<{ $pointer?: string; $prefersMinWidth?: boolean }>`
    display: flex;
    align-items: start;
    width: ${(props) => (props.$prefersMinWidth ? 'fit-content' : undefined)};
    cursor: ${(props) => props.$pointer};
`;

/**
 * A multiselect with just a single set of options to select from.
 *
 * See also: {@link MemoizedMultiSelect}
 */
export function MultiSelect<TValue extends OptionValue>({
    title,
    options,
    selectedOptions,
    onChange,
    className,
    filter,
    customCheckbox,
    optionClass,
    maxSelections,
    showSelectAll,
    singleSelect,
    respectControlKey,
    labelPrefersMinWidth = false,
}: MultiSelectProps<TValue>) {
    const onChangeCheckbox = buildOnChangeCheckbox(
        singleSelect,
        options,
        selectedOptions,
        onChange,
        respectControlKey
    );

    const itemFilter = buildFilter(filter);
    const hasMaxSelected = maxSelections && selectedOptions.length === maxSelections;

    return (
        <div className={className}>
            <ListHeader>{title}</ListHeader>
            {options.length === 0 && <div>No results found</div>}
            {options.length > 0 && (
                <Options>
                    {showSelectAll && options.length > 1 && (
                        <SelectAllCheckbox
                            total={options.length}
                            selected={selectedOptions.length}
                            onSelectAll={() => onChange({ selected: options })}
                            onDeselectAll={() => onChange({ selected: [] })}
                            className="pb-5"
                        />
                    )}
                    {options.map((option, index) => {
                        const isSelected = isOptionSelected(selectedOptions, option);
                        const isEnabled = isSelected || !hasMaxSelected;
                        const pointer = isEnabled ? 'pointer' : '';
                        const isOptionVisible =
                            itemFilter === undefined || itemFilter(option.title);

                        return (
                            <Option
                                key={option.value}
                                style={{ display: isOptionVisible ? 'block' : 'none' }}
                                className={`py-5 ${optionClass ?? ''}`}
                            >
                                <StyledLabel
                                    $pointer={pointer}
                                    $prefersMinWidth={labelPrefersMinWidth}
                                    title={option.title}
                                >
                                    {customCheckbox &&
                                        customCheckbox({
                                            option,
                                            isSelected,
                                            isEnabled,
                                            index,
                                        })}
                                    <Checkbox
                                        onChange={
                                            isEnabled
                                                ? (event: CheckboxChangeEvent) =>
                                                      onChangeCheckbox(
                                                          event,
                                                          option.value,
                                                          event.nativeEvent.ctrlKey ||
                                                              event.nativeEvent.metaKey
                                                      )
                                                : undefined
                                        }
                                        checked={isSelected}
                                        hidden={!!customCheckbox}
                                        label={option.label ?? option.title}
                                    />
                                </StyledLabel>
                            </Option>
                        );
                    })}
                </Options>
            )}
        </div>
    );
}

/**
 * A multiselect with the group provided with just a single set of options to select from.
 */
export function MultiSelectWithGroupOption<TValue extends OptionValue>({
    title,
    options,
    selectedOptions,
    onChange,
    className,
    filter,
    customCheckbox,
    optionClass,
    maxSelections,
}: MultiSelectWithGroupOptionProps<TValue>) {
    const onChangeCheckbox = (event: CheckboxChangeEvent, value: TValue) => {
        let newSelected = selectedOptions;

        if (event.value) {
            // Add to selected options
            newSelected = [...selectedOptions, value];
        } else {
            // Remove from selected options
            newSelected = selectedOptions.filter((selection) => {
                if (isMultiSelectOption(selection)) {
                    return selection.value !== value;
                }
                return selection !== value;
            });
        }

        if (onChange) {
            onChange({
                selected: getSelectedOptionsWithGroupOption(options, newSelected),
            });
        }
    };

    const itemFilter = buildFilter(filter);
    const hasMaxSelected = maxSelections && selectedOptions.length === maxSelections;

    return (
        <div className={className}>
            <ListHeader>{title}</ListHeader>
            <Options>
                {options.map((option, index) => {
                    const isSelected = isOptionSelected(selectedOptions, option);
                    const isEnabled = isSelected || !hasMaxSelected;
                    const pointerClass = isEnabled ? 'cursor-pointer' : '';
                    const isOptionVisible =
                        itemFilter === undefined || itemFilter(option.title);

                    return (
                        <Option
                            key={option.value}
                            style={{ display: isOptionVisible ? 'block' : 'none' }}
                            className={`${optionClass} py-5`}
                        >
                            <label
                                className={`flex align-start ${pointerClass}`}
                                title={option.title}
                            >
                                {customCheckbox &&
                                    customCheckbox({
                                        option,
                                        isSelected,
                                        isEnabled,
                                        index,
                                    })}
                                <Checkbox
                                    onChange={
                                        isEnabled
                                            ? (event: CheckboxChangeEvent) =>
                                                  onChangeCheckbox(event, option.value)
                                            : undefined
                                    }
                                    checked={isSelected}
                                    hidden={!!customCheckbox}
                                    label={
                                        <span>
                                            {option.group} /{' '}
                                            {option.label ?? option.title}
                                        </span>
                                    }
                                />
                            </label>
                        </Option>
                    );
                })}
            </Options>
        </div>
    );
}

/**
 * A multiselect with just a single set of options to select from,
 * displayed as a grid-like layout of buttons.
 *
 * See also: {@link MemoizedButtonMultiSelect}
 */
export function ButtonMultiSelect<TValue extends OptionValue>({
    options,
    selectedOptions,
    onChange,
    className,
    singleSelect,
    maxSelections,
    respectControlKey,
}: MultiSelectProps<TValue>) {
    const onChangeCheckbox = buildOnChangeCheckbox(
        singleSelect,
        options,
        selectedOptions,
        onChange,
        respectControlKey
    );
    const hasMaxSelected = selectedOptions.length === maxSelections;

    return (
        <div className={`flex-column ${className}`}>
            <ButtonMultiSelectOptions>
                {options.map((option) => {
                    const isSelected = isOptionSelected(selectedOptions, option);
                    const isEnabled = isSelected || !hasMaxSelected;
                    const pointerClass = isEnabled ? 'cursor-pointer' : '';

                    return (
                        <ButtonMultiSelectContainer
                            key={option.value}
                            className="ellipsis-1-line"
                        >
                            <label
                                className={`flex align-start ${pointerClass}`}
                                title={option.title}
                            >
                                <SelectableButton
                                    className="p-5"
                                    role="checkbox"
                                    $selected={isSelected}
                                    onClick={
                                        isEnabled
                                            ? (
                                                  event: React.MouseEvent<
                                                      HTMLSpanElement,
                                                      MouseEvent
                                                  >
                                              ) => {
                                                  onChangeCheckbox(
                                                      undefined,
                                                      option.value,
                                                      event.ctrlKey || event.metaKey
                                                  );
                                              }
                                            : undefined
                                    }
                                >
                                    {option.label ?? option.title}
                                </SelectableButton>
                            </label>
                        </ButtonMultiSelectContainer>
                    );
                })}
            </ButtonMultiSelectOptions>
        </div>
    );
}

/**
 * Memoized version of {@link ButtonMultiSelect} component.
 *
 * Note: All props should be stable or memoized in order to benefit from memoization.
 */
export const MemoizedButtonMultiSelect = React.memo(
    ButtonMultiSelect
) as typeof ButtonMultiSelect;

/**
 * Memoized version of {@link MultiSelect} component.
 *
 * Note: All props should be stable or memoized in order to benefit from memoization.
 */
export const MemoizedMultiSelect = React.memo(MultiSelect) as typeof MultiSelect;
